import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Mail, MailCheck, Sparkles, User, PartyPopper } from "lucide-react";

import {
  AuthAlert,
  AuthDivider,
  AuthField,
  AuthFootnote,
  AuthHeading,
  AuthPasswordField,
  AuthPrimaryButton,
  GoogleSignInButton,
  PasswordChecklist,
  PasswordStrength,
} from "./auth-primitives";
import { authCardClass, authFormWidthClass, authLinkClass } from "./auth-tokens";
import {
  DEFAULT_SIGNUP_COPY,
  DEFAULT_AUTH_LEGAL_COPY,
  type SignupFormCopy,
} from "./auth-copy";
import { cn } from "@/lib/utils";

export interface SignupFormProps {
  onSubmit: (email: string, password: string, displayName: string) => Promise<{
    success: boolean;
    alreadyExists?: boolean;
    error?: string;
    requiresEmailConfirmation?: boolean;
  }>;
  onNavigateToSignin: () => void;
  onGoogleSignIn?: () => void;
  googleLoading?: boolean;
  logoSrc?: string;
  /** Translated labels, merged over the English defaults. See `auth-copy.ts`. */
  copy?: Partial<SignupFormCopy>;
}

export const SignupForm = ({
  onSubmit,
  onNavigateToSignin,
  onGoogleSignIn,
  googleLoading = false,
  logoSrc,
  copy,
}: SignupFormProps) => {
  const c = { ...DEFAULT_SIGNUP_COPY, ...copy };
  const legal = { ...DEFAULT_AUTH_LEGAL_COPY, ...c.legal };
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [requiresEmailConfirmation, setRequiresEmailConfirmation] = useState(false);

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError(c.passwordTooShort);
      return;
    }
    if (password !== confirmPassword) {
      setError(c.passwordsMismatch);
      return;
    }

    setLoading(true);
    try {
      const result = await onSubmit(email, password, displayName);
      if (result.success) {
        setSubmittedEmail(email);
        setRequiresEmailConfirmation(!!result.requiresEmailConfirmation);
        setSuccess(true);
        setDisplayName("");
        setEmail("");
        setPassword("");
        setConfirmPassword("");
      } else if (result.error) {
        setError(result.error);
      }
    } catch (err) {
      setError(c.unexpectedError);
    } finally {
      setLoading(false);
    }
  };

  /* ── Success ─────────────────────────────────────────────── */

  if (success) {
    return (
      <div className={authFormWidthClass}>
        <div className={cn(authCardClass, "px-6 py-8 text-center sm:px-8")}>
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: "spring", stiffness: 220, damping: 16 }}
            className="relative mx-auto mb-5 h-20 w-20"
          >
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-primary/25 to-secondary/25 blur-xl" />
            <div className="relative flex h-full w-full items-center justify-center rounded-full bg-gradient-to-br from-primary/12 to-secondary/12 ring-1 ring-primary/20">
              {requiresEmailConfirmation ? (
                <MailCheck className="h-9 w-9 text-primary" />
              ) : (
                <PartyPopper className="h-9 w-9 text-primary" />
              )}
            </div>
            {/* Expanding halo — one soft pulse on entry, then rest. */}
            <motion.span
              initial={{ scale: 0.8, opacity: 0.6 }}
              animate={{ scale: 1.6, opacity: 0 }}
              transition={{ duration: 1.6, ease: "easeOut", repeat: Infinity, repeatDelay: 1.2 }}
              className="absolute inset-0 rounded-full border border-primary/30"
            />
          </motion.div>

          <h1 className="font-display text-[26px] font-semibold tracking-tight text-stone-900 dark:text-stone-50">
            {requiresEmailConfirmation ? c.successTitleConfirmEmail : c.successTitle}
          </h1>
          <p className="mx-auto mt-2 max-w-xs text-sm leading-relaxed text-stone-500 dark:text-stone-400">
            {requiresEmailConfirmation ? (
              <>
                {c.successDescriptionConfirmEmail}{" "}
                <span className="font-medium text-stone-700 dark:text-stone-200">{submittedEmail}</span>.
              </>
            ) : (
              <>{c.successDescription}</>
            )}
          </p>

          <motion.ul
            initial="hidden"
            animate="visible"
            variants={{
              hidden: {},
              visible: { transition: { staggerChildren: 0.09, delayChildren: 0.2 } },
            }}
            className="mx-auto mt-6 max-w-xs space-y-2.5 text-left"
          >
            {(requiresEmailConfirmation
              ? [c.successDescriptionConfirmEmail, c.steps[1], c.steps[2]]
              : c.steps
            ).map((step) => (
              <motion.li
                key={step}
                variants={{
                  hidden: { opacity: 0, x: -8 },
                  visible: { opacity: 1, x: 0, transition: { duration: 0.35 } },
                }}
                className="flex items-start gap-2.5 text-sm text-stone-600 dark:text-stone-300"
              >
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                {step}
              </motion.li>
            ))}
          </motion.ul>

          <div className="mt-7 space-y-2.5">
            <AuthPrimaryButton onClick={onNavigateToSignin} type="button">
              {requiresEmailConfirmation ? c.successActionConfirmEmail : c.successAction}
              <ArrowRight className="h-4 w-4 opacity-70" />
            </AuthPrimaryButton>
            <button
              type="button"
              className={`w-full text-center text-[13px] ${authLinkClass}`}
              onClick={() => {
                setSuccess(false);
                setError(null);
              }}
            >
              Use a different email
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ── Form ─────────────────────────────────────────────────── */

  return (
    <div className={authFormWidthClass}>
      <AuthHeading
        icon={<img src={logoSrc || "/icon.png"} alt="" className="h-9 w-9 object-contain" />}
        title={c.title}
        description={c.description}
      />

      <motion.form
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.08, ease: [0.16, 1, 0.3, 1] }}
        onSubmit={handleSignup}
        className="mt-7 space-y-4"
      >
        <AuthField
          name="displayName"
          label={c.displayNameLabel}
          icon={<User className="h-[18px] w-[18px]" />}
          value={displayName}
          onChange={setDisplayName}
          placeholder={c.displayNamePlaceholder}
          autoComplete="name"
        />

        <AuthField
          name="email"
          label={c.emailLabel}
          icon={<Mail className="h-[18px] w-[18px]" />}
          type="email"
          value={email}
          onChange={setEmail}
          placeholder="you@example.com"
          autoComplete="email"
          required
        />

        <div className="space-y-2">
          <AuthPasswordField
            name="password"
            label={c.passwordLabel}
            value={password}
            onChange={setPassword}
            autoComplete="new-password"
            required
          showPasswordLabel={c.showPasswordLabel}
          hidePasswordLabel={c.hidePasswordLabel}
        />
          <PasswordStrength password={password} labels={c.passwordStrengthLabels} />
          <PasswordChecklist password={password} labels={c.passwordRuleLabels} />
        </div>

        <AuthPasswordField
          name="confirmPassword"
          label={c.confirmPasswordLabel}
          value={confirmPassword}
          onChange={setConfirmPassword}
          autoComplete="new-password"
          required
          hint={c.matchHint}
          showPasswordLabel={c.showPasswordLabel}
          hidePasswordLabel={c.hidePasswordLabel}
        />

        <AuthAlert message={error} />

        <div className="pt-1">
          <AuthPrimaryButton loading={loading} loadingLabel={c.creating}>
            <Sparkles className="h-4 w-4" />
            {c.submit}
            <ArrowRight className="h-4 w-4 opacity-70 transition-transform duration-200 group-hover:translate-x-0.5" />
          </AuthPrimaryButton>
        </div>

        {onGoogleSignIn ? (
          <>
            <AuthDivider label={legal.orContinueWith} />
            <GoogleSignInButton
              loading={googleLoading}
              onClick={onGoogleSignIn}
              label={c.googleLabel}
            />
          </>
        ) : null}
      </motion.form>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.2 }}
        className="mt-6 space-y-3"
      >
        <p className="text-center text-sm text-stone-500 dark:text-stone-400">
          {c.haveAccount}{" "}
          <button type="button" className={authLinkClass} onClick={onNavigateToSignin}>
            {c.signInInstead}
          </button>
        </p>
        <AuthFootnote>
          {legal.termsPrefix}{" "}
          <a href="/terms" className="underline underline-offset-2 hover:text-stone-600">
            {legal.termsLink}
          </a>{" "}
          {legal.andPrefix}{" "}
          <a href="/privacy" className="underline underline-offset-2 hover:text-stone-600">
            {legal.privacyLink}
          </a>
          .
        </AuthFootnote>
      </motion.div>
    </div>
  );
};

export default SignupForm;